
import React, { useState, useRef, useEffect, useCallback } from "react";
import * as XLSX from "xlsx";
import api from "../../api";
import "../../CSS/ConsignmentRateUpdate.css";

/* ---------------- reference data ----------------
   Branch and Party lists are fetched live from the same endpoints
   ConsignmentBooking.jsx uses (/get-booking-places and /get-partylist /
   /get-partylistBranchWise). All scope counting, rate resolution
   (Rate / Rate_Details master) and row validation happen on the backend
   via ConsignmentRateUpdateController — GET /rate-update/scope-count,
   POST /rate-update/preview and POST /rate-update/apply — so nothing is
   mocked locally any more. */
const FIELDS = [
  { k: "cnr", l: "Consignment No", req: true },
  { k: "consignor", l: "Consignor", req: false },
  { k: "consignee", l: "Consignee", req: false },
  { k: "pcs", l: "PCS", req: false },
  { k: "weight", l: "Weight", req: false },
  { k: "volumetric", l: "Volumetric (L*B*H)", req: false },
  { k: "amount", l: "Amount", req: true },
];
const VOL_DIVISOR = 5000;
const EMPTY_MAPPING = { cnr: "", consignor: "", consignee: "", pcs: "", weight: "", volumetric: "", amount: "" };

/* ---------------- helpers ---------------- */
function num(v) {
  const n = parseFloat(String(v).replace(/[^0-9.-]/g, ""));
  return isNaN(n) ? null : n;
}
function volWeight(v) {
  if (v == null || String(v).trim() === "") return null;
  const s = String(v).toLowerCase().replace(/[×x]/g, "*").replace(/\s/g, "");
  if (/[*/]/.test(s)) {
    const p = s.split(/[*/]/).map(parseFloat).filter((n) => !isNaN(n));
    if (p.length >= 3) return +((p[0] * p[1] * p[2]) / VOL_DIVISOR).toFixed(2);
  }
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
}
function esc(s) {
  return String(s == null ? "" : s).replace(/[&<>]/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[m]));
}
function parseDelimited(text) {
  const lines = text.replace(/\r/g, "").split("\n").filter((l) => l.trim().length);
  const delim = lines[0] && lines[0].indexOf("\t") > -1 ? "\t" : ",";
  return lines.map((line) => {
    const out = [];
    let cur = "",
      q = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (q && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else q = !q;
      } else if (ch === delim && !q) {
        out.push(cur);
        cur = "";
      } else cur += ch;
    }
    out.push(cur);
    return out.map((s) => s.trim());
  });
}
function toCSV(rowsArr) {
  return rowsArr
    .map((r) =>
      r
        .map((v) => {
          v = String(v == null ? "" : v);
          return /[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v;
        })
        .join(",")
    )
    .join("\n");
}
function downloadBlob(name, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* ---------------- Combo (custom dropdown) ---------------- */
function Combo({ id, options, value, placeholder, searchable = true, small = false, onCommit }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hi, setHi] = useState(0);
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value));
  const filtered = searchable
    ? options.filter((o) => String(o.label || "").toLowerCase().includes(query.toLowerCase().trim()))
    : options;

  useEffect(() => {
    if (!open) return;
    const fi = options.findIndex((o) => String(o.value) === String(value));
    setHi(fi < 0 ? 0 : fi);
    setQuery("");
    if (searchable) setTimeout(() => searchRef.current && searchRef.current.focus(), 20);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function onDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  function commit(val) {
    onCommit(val);
    setOpen(false);
  }
  function onKey(e) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHi((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHi((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const o = filtered[hi];
      if (o) commit(o.value);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  }

  return (
    <div className="combo-wrap" ref={wrapRef}>
      <div className={"combo" + (small ? " sm" : "")} onClick={() => setOpen((o) => !o)}>
        <span className={"combo-val" + (selected && selected.value !== "" ? "" : " ph")}>{selected ? selected.label : placeholder}</span>
        <svg className="combo-caret" viewBox="0 0 11 7">
          <path d="M1 1l4.5 4.5L10 1" stroke="#94a3b8" strokeWidth="1.6" fill="none" strokeLinecap="round" />
        </svg>
      </div>
      <div className={"combo-panel" + (open ? " open" : "")}>
        {searchable && (
          <div className="combo-search">
            <input
              ref={searchRef}
              autoComplete="off"
              placeholder="Type to filter…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setHi(0);
              }}
              onKeyDown={onKey}
            />
          </div>
        )}
        <div className="combo-list">
          {filtered.length ? (
            filtered.map((o, i) => (
              <div
                key={String(o.value) + i}
                className={"combo-opt" + (String(o.value) === String(value) ? " sel " : " ") + (i === hi ? "hi" : "")}
                onMouseEnter={() => setHi(i)}
                onClick={() => commit(o.value)}
              >
                {o.label}
              </div>
            ))
          ) : (
            <div style={{ padding: 12, textAlign: "center", color: "var(--muted)", fontSize: 12.5 }}>No match</div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Toasts ---------------- */
function ToastWrap({ toasts }) {
  const ic = { ok: "✓", err: "✕", warn: "⚠", info: "ℹ" };
  return (
    <div className="toast-wrap">
      {toasts.map((t) => (
        <div key={t.id} className={"toast " + t.type}>
          <span style={{ fontSize: 15 }}>{ic[t.type] || "ℹ"}</span>
          {t.msg}
        </div>
      ))}
    </div>
  );
}

/* ---------------- Main component ---------------- */
export default function ConsignmentRateUpdate() {
  const [step, setStep] = useState(1);

  // scope
  const [scBranch, setScBranch] = useState(""); // holds the branch's place_Id (or "" for All Branches)
  const [scParty, setScParty] = useState(""); // holds the party's Party_id (or "" for All Parties)
  const [scFrom, setScFrom] = useState("2026-06-01");
  const [scTo, setScTo] = useState("2026-07-31");

  // Branch & Party dropdown options — fetched live, same endpoints/shape as
  // ConsignmentBooking.jsx. Party list reloads branch-wise whenever Branch
  // changes (fetchPartiesByBranch), falling back to the full list while
  // "All Branches" is selected (fetchAllPartiesFlat) — same behaviour as the
  // Search Filters panel there.
  const [branchOpts, setBranchOpts] = useState([]);
  const [partyOpts, setPartyOpts] = useState([]);
  const [branchLoading, setBranchLoading] = useState(true);
  const [partyLoading, setPartyLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/get-booking-places");
        setBranchOpts(
          (res.data || [])
            .map((b) => {
              const id = b.place_Id ?? b.Place_Id;
              const name = b.place_name || b.Place_name || "";
              return { value: id, label: name };
            })
            .filter((o) => o.value != null && o.label) // drop any record missing id/name
        );
      } catch (err) {
        console.error("Failed to fetch branches:", err);
      } finally {
        setBranchLoading(false);
      }
    })();
  }, []);

  // Party list — reloads whenever the selected Branch changes, scoped to
  // that branch; resets to the full list when "All Branches" is selected.
  useEffect(() => {
    setScParty(""); // previous branch's party selection no longer applies
    let cancelled = false;
    setPartyLoading(true);
    (async () => {
      try {
        let list;
        if (scBranch) {
          const res = await api.get("/get-partylistBranchWise", { params: { billingBranchId: scBranch } });
          list = res.data || [];
        } else {
          const res = await api.get("/get-partylist");
          list = res.data || [];
        }
        const opts = list
          .map((p) => {
            const id = p.party_Id ?? p.Party_Id ?? p.party_id ?? p.PartyId;
            const name = p.party_Name || p.Party_Name || "";
            return { value: id, label: name };
          })
          .filter((o) => o.value != null && o.label); // drop any record missing id/name
        if (!cancelled) setPartyOpts(opts);
      } catch (err) {
        console.error("Failed to fetch parties:", err);
        if (!cancelled) setPartyOpts([]);
      } finally {
        if (!cancelled) setPartyLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scBranch]);

  // import / map
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState(EMPTY_MAPPING);
  const [fileName, setFileName] = useState("");
  const [useMaster, setUseMaster] = useState(true);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // preview
  const [preview, setPreview] = useState([]);
  const [lastResult, setLastResult] = useState([]);

  // toasts
  const [toasts, setToasts] = useState([]);
  const toast = useCallback((msg, type) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((t) => [...t, { id, msg, type: type || "info" }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 2600);
  }, []);

  /* ---------- scope ---------- */
  // Step 1 — live count of unbilled consignments matching the selected
  // Branch / Party / Booking-date range, via GET /rate-update/scope-count.
  const [scopeCount, setScopeCount] = useState(0);
  const [scopeLoading, setScopeLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setScopeLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get("/rate-update/scope-count", {
          params: {
            branchId: scBranch || undefined,
            partyId: scParty || undefined,
            fromDate: scFrom || undefined,
            toDate: scTo || undefined,
          },
        });
        if (!cancelled) setScopeCount(res.data?.count ?? 0);
      } catch (err) {
        console.error("Failed to fetch scope count:", err);
        if (!cancelled) setScopeCount(0);
      } finally {
        if (!cancelled) setScopeLoading(false);
      }
    }, 250); // debounce while the user is still picking filters
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [scBranch, scParty, scFrom, scTo]);

  /* ---------- stepper ---------- */
  const stepDefs = [
    ["Select Scope", "Branch · Party · Period"],
    ["Import & Map", "Upload & map columns"],
    ["Validate", "Preview rates"],
    ["Update", "Ready for booking"],
  ];

  function goStep(n) {
    setStep(n);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  function toStep(n) {
    if (n === 3) {
      if (!mapping.cnr || !mapping.amount) {
        toast("Map Consignment and Amount first", "warn");
        return;
      }
      if (!rows.length) {
        toast("Import a sheet first", "warn");
        return;
      }
      buildPreview();
    }
    goStep(n);
  }

  /* ---------- file / parse ---------- */
  function setParsed(aoa, fname) {
    if (!aoa || !aoa.length) {
      toast("The sheet appears to be empty", "warn");
      return;
    }
    const hdrs = aoa[0].map((h) => String(h).trim());
    const rws = aoa.slice(1).filter((r) => r.some((c) => String(c).trim().length));
    setHeaders(hdrs);
    setRows(rws);
    if (fname !== undefined) setFileName(fname);
    setMapping(autoMap(hdrs));
  }
  function autoMap(hdrs) {
    const m = { ...EMPTY_MAPPING };
    hdrs.forEach((h, i) => {
      const s = h.toLowerCase();
      if (!m.cnr && /(consignment|cnr|docket|awb|lr\b)/.test(s)) m.cnr = String(i);
      else if (!m.consignor && /(consignor|shipper|sender|from\s*party)/.test(s)) m.consignor = String(i);
      else if (!m.consignee && /(consignee|receiver|to\s*party|delivery\s*party)/.test(s)) m.consignee = String(i);
      else if (!m.volumetric && /(volumetric|volume|dimension|dims|l\*b\*h|lbh|cbm|cft)/.test(s)) m.volumetric = String(i);
      else if (!m.pcs && /(pcs|piece|qty|package|pkg|nop)/.test(s)) m.pcs = String(i);
      else if (!m.weight && /(weight|wt|kg)/.test(s)) m.weight = String(i);
      else if (!m.amount && /(amount|amt|rate|value|freight)/.test(s)) m.amount = String(i);
    });
    return m;
  }
  function readFile(f) {
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    const reader = new FileReader();
    if ((ext === "xlsx" || ext === "xls") && typeof XLSX !== "undefined") {
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
          setParsed(aoa, f.name);
        } catch (err) {
          toast("Could not read the Excel file", "err");
        }
      };
      reader.readAsArrayBuffer(f);
    } else {
      reader.onload = (e) => setParsed(parseDelimited(e.target.result), f.name);
      reader.readAsText(f);
    }
  }
  function onFileInputChange(e) {
    if (e.target.files && e.target.files[0]) readFile(e.target.files[0]);
  }
  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) readFile(e.dataTransfer.files[0]);
  }
  function loadSample() {
    const csv = [
      "Consignment No,Consignor,Consignee,PCS,Weight,Volumetric (L*B*H),Amount",
      "STL-2607-00101,Tata Motors,Tata Spares Hub,3,120,,",
      "STL-2607-00102,Tata Motors,Tata Air Cargo,5,260,100*80*60,",
      "STL-2607-00106,Sundaram Valeo,SVP Depot,4,70,,850",
      "STL-2607-00110,Bosch Ltd,Bosch Chennai,9,50,120*100*90,",
      "STL-2607-00120,Maruti Suzuki,MSI Depot,2,50,,1000",
      "STL-2607-09999,Unknown,Unknown,1,20,,500",
    ].join("\n");
    setParsed(parseDelimited(csv), "sample-rate-sheet.csv");
    toast("Sample sheet loaded", "info");
  }
  function downloadTemplate() {
    const data = [
      ["Consignment No", "Consignor", "Consignee", "PCS", "Weight", "Volumetric (L*B*H)", "Amount"],
      ["STL-2607-00101", "Tata Motors", "Tata Spares Hub", "3", "120", "40*30*25", ""],
      ["STL-2607-00106", "Sundaram Valeo", "SVP Depot", "4", "70", "", "850"],
    ];
    if (typeof XLSX !== "undefined") {
      const ws = XLSX.utils.aoa_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "RateUpdate");
      XLSX.writeFile(wb, "rate-update-template.xlsx");
    } else {
      downloadBlob("rate-update-template.csv", toCSV(data));
    }
    toast("Template downloaded", "ok");
  }

  const mapReady = !!(mapping.cnr && mapping.amount && rows.length);
  const [previewLoading, setPreviewLoading] = useState(false);

  /* ---------- preview / compute ---------- */
  // Step 3 — POST /rate-update/preview: the frontend only maps the sheet's
  // columns (below); consignment_master lookup, billed/scope checks and
  // Rate Master resolution all happen server-side.
  async function buildPreview() {
    const g = (k) => mapping[k] !== "";
    const sheetRows = rows.map((r) => ({
      cnr: String(r[+mapping.cnr] || "").trim(),
      consignor: g("consignor") ? String(r[+mapping.consignor] || "").trim() : "",
      consignee: g("consignee") ? String(r[+mapping.consignee] || "").trim() : "",
      pcs: g("pcs") ? num(r[+mapping.pcs]) : null,
      weight: g("weight") ? num(r[+mapping.weight]) : null,
      volumetric: g("volumetric") ? String(r[+mapping.volumetric] || "").trim() : "",
      amount: num(r[+mapping.amount]),
    }));

    setPreviewLoading(true);
    try {
      const res = await api.post("/rate-update/preview", {
        branchId: scBranch || null,
        partyId: scParty || null,
        fromDate: scFrom || null,
        toDate: scTo || null,
        useMaster,
        rows: sheetRows,
      });
      const out = (res.data?.rows || []).map((p) => ({
        cnr: p.cnr,
        consignor: p.consignor,
        consignee: p.consignee,
        pcs: p.pcs,
        wtSheet: null,
        volRaw: p.volRaw,
        volWt: p.volWt,
        amount: p.sheetAmount,
        status: p.status,
        final: p.finalAmount,
        source: p.source,
        party: p.partyName,
        weight: p.weight,
        chg: p.chgWt,
        perKg: p.perSlabRate,
        rateId: p.rateId,
      }));
      setPreview(out);
    } catch (err) {
      console.error("Failed to validate rate update:", err);
      toast(err?.response?.data?.message || "Failed to validate the sheet against the server", "err");
    } finally {
      setPreviewLoading(false);
    }
  }

  // rebuild preview when useMaster toggles while already on preview step
  useEffect(() => {
    if (step >= 3 && rows.length) buildPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useMaster]);

  function editFinal(i, v) {
    const n = num(v);
    setPreview((p) => {
      const copy = p.slice();
      copy[i] = { ...copy[i], final: n == null ? 0 : n };
      return copy;
    });
  }

  const counts = { update: 0, fixed: 0, nomatch: 0, billed: 0, err: 0 };
  preview.forEach((p) => {
    counts[p.status] = (counts[p.status] || 0) + 1;
  });
  const stLbl = { update: "Will update", fixed: "Fixed rate", nomatch: "Not matched", billed: "Already billed", err: "Error" };
  const stCls = { update: "st-update", fixed: "st-fixed", nomatch: "st-nomatch", billed: "st-billed", err: "st-err" };
  const applyCount = counts.update + counts.fixed;

  /* ---------- apply ---------- */
  const [applying, setApplying] = useState(false);

  // Step 4 — POST /rate-update/apply: send only the rows the preview marked
  // "update"/"fixed" (with any edits the user made to Final Amount). The
  // backend re-checks bill status at write time and writes Amount/Taxable_Amt
  // inside a transaction. Rows that never qualified (nomatch/billed/err) are
  // kept out of the request and logged locally with their skip reason.
  async function applyUpdate() {
    const toSend = preview
      .filter((p) => p.status === "update" || p.status === "fixed")
      .map((p) => ({
        cnr: p.cnr,
        finalAmount: p.final || 0,
        consignor: p.consignor || "",
        consignee: p.consignee || "",
        source: p.source,
      }));

    const localSkips = preview
      .filter((p) => p.status !== "update" && p.status !== "fixed")
      .map((p) => ({
        cnr: p.cnr,
        consignor: p.consignor || "",
        consignee: p.consignee || "",
        weight: p.weight || "",
        chg: p.chg || "",
        amount: p.final || "",
        source: p.source,
        result: { nomatch: "Skipped — not in scope", billed: "Skipped — already billed", err: "Skipped — " + p.source }[p.status] || "Skipped",
      }));

    if (toSend.length === 0) {
      setLastResult(localSkips);
      goStep(4);
      return;
    }

    setApplying(true);
    try {
      const res = await api.post("/rate-update/apply", toSend);
      const applied = (res.data?.results || []).map((r) => ({
        cnr: r.cnr,
        consignor: r.consignor || "",
        consignee: r.consignee || "",
        weight: r.weight ?? "",
        chg: r.chgWt ?? "",
        amount: r.amount ?? "",
        source: r.source,
        result: r.result,
      }));
      setLastResult([...applied, ...localSkips]);
      goStep(4);
      const updatedCountFromApi = res.data?.updatedCount ?? applied.filter((r) => r.result === "Updated").length;
      toast(updatedCountFromApi + " rate" + (updatedCountFromApi === 1 ? "" : "s") + " updated", "ok");
    } catch (err) {
      console.error("Failed to apply rate update:", err);
      toast(err?.response?.data?.message || "Failed to apply the rate update", "err");
    } finally {
      setApplying(false);
    }
  }
  function downloadLog() {
    const head = ["Consignment", "Consignor", "Consignee", "Weight", "Chg. Wt", "Amount", "Source", "Result"];
    const body = lastResult.map((r) => [r.cnr, r.consignor, r.consignee, r.weight, r.chg, r.amount, r.source, r.result]);
    downloadBlob("rate-update-log.csv", toCSV([head, ...body]));
    toast("Log downloaded", "ok");
  }
  function startOver() {
    setHeaders([]);
    setRows([]);
    setPreview([]);
    setMapping(EMPTY_MAPPING);
    setFileName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    goStep(1);
  }

  const updatedCount = applyCount; // for success screen we recompute from lastResult length of "Updated"
  const successUpdated = lastResult.filter((r) => r.result === "Updated").length;
  const successSkipped = lastResult.length - successUpdated;

  return (
    <div className="cru-root">
      {/* <header className="hdr">
        <div className="logo">
          <div className="logo-mark">
            <svg viewBox="0 0 24 24">
              <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm13.5 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z" />
            </svg>
          </div>
          <div>
            <div className="logo-name">
              Saami <span>Tradestar</span>
            </div>
            <div className="logo-pvt">Rate Update</div>
          </div>
        </div>
        <div className="hdr-user">
          <div className="hdr-av">BL</div>Billing
        </div>
      </header> */}

      {/* <div className="bc">
        <span>Billing</span>
        <span className="bc-sep">›</span>
        <span className="bc-cur">Consignment Rate Update</span>
      </div> */}

      <div className="page">
        <div className="pg-title">
          <h1>Consignment Rate Update (from Excel)</h1>
          <p>
            Bulk-update rates for <b>unbilled</b> consignments by branch, party &amp; period. Rate per kg is auto-calculated from the Rate Master on the{" "}
            <b>chargeable weight</b> (higher of actual vs volumetric L×B×H ÷ 5000); where no rate is defined, the fixed amount from your sheet is used. Once
            updated, consignments are ready for booking.
          </p>
        </div>

        {/* stepper */}
        <div className="stepper">
          {stepDefs.map((d, i) => {
            const n = i + 1;
            const cls = n === step ? "active" : n < step ? "done" : "";
            return (
              <div className={"step " + cls} key={n}>
                <div className="step-n">{n < step ? "✓" : n}</div>
                <div>
                  <div className="step-t">{d[0]}</div>
                  <div className="step-s">{d[1]}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* STEP 1 */}
        {step === 1 && (
          <div className="card">
            <div className="band">
              <div className="band-icon">
                <svg viewBox="0 0 24 24" strokeWidth="2">
                  <path d="M22 3H2l8 9.46V19l4 2v-8.54z" />
                </svg>
              </div>
              <div>
                <div className="band-title">Step 1 · Select Scope</div>
                <div className="band-sub">Filter which unbilled consignments this update applies to</div>
              </div>
            </div>
            <div className="body">
              {/* Branch / Party no longer scope the update (backend ignores
                  them now — see ConsignmentRateUpdateController). Left
                  commented out, not removed, in case they come back. */}
              {/* <div className="g2">
                <div className="field">
                  <label>Branch</label>
                  <Combo
                    id="sc-branch"
                    value={scBranch}
                    placeholder={branchLoading ? "Loading branches…" : "All Branches"}
                    options={[{ value: "", label: "All Branches" }, ...branchOpts]}
                    onCommit={setScBranch}
                  />
                </div>
                <div className="field">
                  <label>Party</label>
                  <Combo
                    id="sc-party"
                    value={scParty}
                    placeholder={partyLoading ? "Loading parties…" : "All Parties"}
                    options={[{ value: "", label: "All Parties" }, ...partyOpts]}
                    onCommit={setScParty}
                  />
                </div>
              </div> */}
              <div className="g2">
                <div className="field">
                  <label>Booking Period — From</label>
                  <input type="date" value={scFrom} onChange={(e) => setScFrom(e.target.value)} />
                </div>
                <div className="field">
                  <label>Booking Period — To</label>
                  <input type="date" value={scTo} onChange={(e) => setScTo(e.target.value)} />
                </div>
              </div>
              <div className="scope-note">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 18, height: 18, flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4M12 8h.01" />
                </svg>
                <span>
                  {scopeLoading ? (
                    "Counting unbilled consignments in scope…"
                  ) : (
                    <>
                      <b>{scopeCount}</b> unbilled consignment{scopeCount === 1 ? "" : "s"} in scope will be considered for rate update.
                    </>
                  )}
                </span>
              </div>
            </div>
            <div className="foot">
              <span></span>
              <button className="btn btn-primary" onClick={() => toStep(2)}>
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div className="card">
            <div className="band">
              <div className="band-icon">
                <svg viewBox="0 0 24 24" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>
              <div>
                <div className="band-title">Step 2 · Import Sheet &amp; Map Columns</div>
                <div className="band-sub">Upload your Excel/CSV, then map the columns</div>
              </div>
            </div>
            <div className="body">
              <div style={{ display: "flex", gap: 9, flexWrap: "wrap", marginBottom: 12 }}>
                <button className="btn btn-ghost btn-sm" onClick={downloadTemplate}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>{" "}
                  Download Template
                </button>
                <button className="btn btn-ghost btn-sm" onClick={loadSample}>
                  Load Sample Sheet
                </button>
              </div>

              <div
                className={"drop" + (dragOver ? " over" : "")}
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                }}
                onDrop={onDrop}
              >
                <div className="di">📄</div>
                <div className="dt">Click to upload or drop your file here</div>
                <div className="ds">Accepts .xlsx, .xls, .csv — first sheet is read</div>
              </div>
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv,.tsv" className="hidden" onChange={onFileInputChange} />

              {fileName && (
                <div className="file-chip">
                  ✓ {fileName} — {rows.length} row(s), {headers.length} column(s)
                </div>
              )}

              {headers.length > 0 && (
                <div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "var(--navy)", margin: "18px 0 10px" }}>
                    Map your columns{" "}
                    <span style={{ fontWeight: 600, color: "var(--muted)", fontSize: 12 }}>— Consignment &amp; Amount are required</span>
                  </div>
                  <div className="map-grid">
                    {FIELDS.map((f) => {
                      const opts = (f.req ? [] : [{ value: "", label: "— None —" }]).concat(
                        headers.map((h, i) => ({ value: String(i), label: h || "Column " + (i + 1) }))
                      );
                      return (
                        <div className={"map-cell" + (f.req ? " reqd" : "")} key={f.k}>
                          <div className="mc-l">
                            {f.l}{" "}
                            {f.req ? (
                              <>
                                <span className="req">*</span>
                                <span className="badge bg-req">REQUIRED</span>
                              </>
                            ) : (
                              <span className="badge bg-opt">OPTIONAL</span>
                            )}
                          </div>
                          <Combo
                            id={"map-" + f.k}
                            small
                            value={mapping[f.k] || ""}
                            placeholder={f.req ? "— Select column —" : "— None —"}
                            searchable={headers.length > 6}
                            options={opts}
                            onCommit={(v) => setMapping((m) => ({ ...m, [f.k]: v }))}
                          />
                        </div>
                      );
                    })}
                  </div>

                  <div className="opt-toggle">
                    <div>
                      <div className="ot-t">Use Rate Master when available</div>
                      <div className="ot-s">Auto-calculate amount = per-kg rate × weight. Falls back to the sheet's fixed Amount when the master has no rate.</div>
                    </div>
                    <label className="switch">
                      <input type="checkbox" checked={useMaster} onChange={(e) => setUseMaster(e.target.checked)} />
                      <span className="slider"></span>
                    </label>
                  </div>

                  {rows.length > 0 && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", margin: "14px 0 4px", textTransform: "uppercase", letterSpacing: 0.3 }}>
                        Preview (first rows, mapped)
                      </div>
                      <table className="mini-tbl">
                        <thead>
                          <tr>
                            {FIELDS.filter((f) => mapping[f.k] !== "").map((f) => (
                              <th key={f.k}>{f.l}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.slice(0, 4).map((r, ri) => (
                            <tr key={ri}>
                              {FIELDS.filter((f) => mapping[f.k] !== "").map((f) => (
                                <td key={f.k}>{r[+mapping[f.k]] !== undefined ? String(r[+mapping[f.k]]) : ""}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="foot">
              <button className="btn btn-ghost" onClick={() => toStep(1)}>
                ← Back
              </button>
              <button className="btn btn-primary" disabled={!mapReady} onClick={() => toStep(3)}>
                Validate &amp; Preview →
              </button>
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div className="card">
            <div className="band">
              <div className="band-icon">
                <svg viewBox="0 0 24 24" strokeWidth="2">
                  <path d="M9 11l3 3L22 4" />
                  <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                </svg>
              </div>
              <div>
                <div className="band-title">Step 3 · Validate &amp; Preview</div>
                <div className="band-sub">Review matched consignments and the rate to be applied</div>
              </div>
            </div>
            <div className="body">
              <div className="sum-row">
                <div className="sum">
                  <div className="sv">{counts.update}</div>
                  <div className="sl">Rate from Master</div>
                </div>
                <div className="sum fixed">
                  <div className="sv">{counts.fixed}</div>
                  <div className="sl">Fixed (sheet)</div>
                </div>
                <div className="sum nomatch">
                  <div className="sv">{counts.nomatch}</div>
                  <div className="sl">Not in scope</div>
                </div>
                <div className="sum billed">
                  <div className="sv">{counts.billed}</div>
                  <div className="sl">Already billed</div>
                </div>
                <div className="sum err">
                  <div className="sv">{counts.err}</div>
                  <div className="sl">Errors</div>
                </div>
              </div>
              {previewLoading && (
                <div style={{ padding: "18px 4px", color: "var(--muted)", fontSize: 13 }}>Validating rows against the server…</div>
              )}
              <div className="tbl-scroll">
                <table className="prev">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Consignment</th>
                      <th>Consignor</th>
                      <th>Consignee</th>
                      <th>Party</th>
                      <th className="r">PCS</th>
                      <th className="r">Weight</th>
                      <th className="r">Volumetric</th>
                      <th className="r">Chg. Wt</th>
                      <th className="r">₹/kg</th>
                      <th className="r">Sheet Amt</th>
                      <th className="r">Final Amount</th>
                      <th>Source</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!previewLoading && preview.map((p, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td className="mono" dangerouslySetInnerHTML={{ __html: esc(p.cnr) || "—" }} />
                        <td dangerouslySetInnerHTML={{ __html: esc(p.consignor) || "—" }} />
                        <td dangerouslySetInnerHTML={{ __html: esc(p.consignee) || "—" }} />
                        <td>{p.party || "—"}</td>
                        <td className="r">{p.pcs != null ? p.pcs : ""}</td>
                        <td className="r">{p.weight != null ? p.weight : p.wtSheet != null ? p.wtSheet : ""}</td>
                        <td className="r">
                          {p.volRaw ? (
                            <>
                              {esc(p.volRaw)}
                              {p.volWt != null ? <span style={{ color: "var(--muted)" }}> ({p.volWt}kg)</span> : null}
                            </>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="r">
                          <b>{p.chg != null ? p.chg : "—"}</b>
                        </td>
                        <td className="r">{p.perKg != null ? p.perKg : "—"}</td>
                        <td className="r mono">{p.amount != null ? p.amount.toLocaleString("en-IN") : "—"}</td>
                        <td className="r">
                          {p.status === "update" || p.status === "fixed" ? (
                            <input
                              className="amt-in mono"
                              type="number"
                              defaultValue={p.final}
                              onChange={(e) => editFinal(i, e.target.value)}
                            />
                          ) : p.final != null ? (
                            <span className="mono">{p.final.toLocaleString("en-IN")}</span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          <span className="src">
                            {/Master/.test(p.source) ? <span className="k">{p.source}</span> : p.source}
                          </span>
                        </td>
                        <td>
                          <span className={"st " + stCls[p.status]}>{stLbl[p.status]}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 10 }}>
                Only <b>unbilled</b> consignments within your selected scope are updated. Rows that don't match, are already billed, or are missing a rate are
                skipped. You can edit any Final Amount before applying.
              </div>
            </div>
            <div className="foot">
              <button className="btn btn-ghost" onClick={() => toStep(2)}>
                ← Back
              </button>
              <button className="btn btn-green" disabled={applyCount === 0 || applying} onClick={applyUpdate}>
                {applying ? "Updating…" : applyCount ? "Update " + applyCount + " Rate" + (applyCount > 1 ? "s" : "") : "Nothing to update"}
              </button>
            </div>
          </div>
        )}

        {/* STEP 4 */}
        {step === 4 && (
          <div className="card">
            <div className="band">
              <div className="band-icon">
                <svg viewBox="0 0 24 24" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <div>
                <div className="band-title">Step 4 · Done</div>
                <div className="band-sub">Rates updated</div>
              </div>
            </div>
            <div className="body">
              <div className="success">
                <div className="tick">
                  <svg viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h2>
                  {successUpdated} consignment{successUpdated === 1 ? "" : "s"} updated
                </h2>
                <p>Rates have been applied to the unbilled consignments in your selected scope.</p>
                <div className="ready-note">✓ These consignments are now Ready for Booking</div>
                <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 14 }}>
                  {successSkipped} row(s) were skipped (not matched, already billed, or missing rate). Download the log for details.
                </p>
              </div>
            </div>
            <div className="foot">
              <button className="btn btn-ghost" onClick={startOver}>
                ↺ Start Over
              </button>
              <button className="btn btn-primary" onClick={downloadLog}>
                Download Result Log
              </button>
            </div>
          </div>
        )}
      </div>

      <ToastWrap toasts={toasts} />
    </div>
  );
}
